import React from 'react';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface CalendarHeaderProps {
  currentDate: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onTodayClick: () => void;
}

/**
 * iOS 风格的日历头部组件
 */
export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  onPrevMonth,
  onNextMonth,
  onTodayClick,
}) => {
  const { t } = useTranslation(['v0/calendar', 'v0/common']);
  const navigate = useNavigate();

  // 格式化当前月份和年份
  const { i18n } = useTranslation();
  const monthYear = i18n.language === 'zh'
    ? format(currentDate, 'yyyy年MM月', { locale: zhCN })
    : format(currentDate, 'MMMM yyyy');

  return (
    <div className="px-4 py-2">
      <div className="flex items-center justify-between">
        <motion.div
          className="flex items-center space-x-3"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* 返回按钮 */}
          <motion.button
            onClick={() => navigate('/v3/taskv2')}
            className="p-1 rounded-full text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 transition-colors"
            whileTap={{ scale: 0.9 }}
            title="返回规划"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </motion.button>

          <h2 className="text-ios-title2 font-semibold">{monthYear}</h2>
        </motion.div>

        <div className="flex space-x-2">
          <motion.button
            onClick={onTodayClick}
            className="px-3 py-1 text-ios-subhead text-ios-blue dark:text-ios-dark-blue font-medium rounded-full bg-white/80 dark:bg-ios-dark-gray5/80 border border-ios-gray5 dark:border-ios-dark-gray4 shadow-ios-sm"
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            {t('v0/calendar:buttons.today', '今天')}
          </motion.button>

          <motion.div
            className="flex rounded-full overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray4 shadow-ios-sm"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <motion.button
              onClick={onPrevMonth}
              className="w-8 h-8 flex items-center justify-center bg-white/80 dark:bg-ios-dark-gray5/80 text-ios-gray dark:text-ios-dark-gray"
              whileTap={{ scale: 0.9 }}
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.button>

            <motion.button
              onClick={onNextMonth}
              className="w-8 h-8 flex items-center justify-center bg-white/80 dark:bg-ios-dark-gray5/80 text-ios-gray dark:text-ios-dark-gray"
              whileTap={{ scale: 0.9 }}
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
